import { useEffect } from 'react'
import { useRouter } from 'next/router'

import type { GetServerSideProps } from 'next'

import { useAuth } from '@/contexts/AuthContext'

import Loading from '@/components/Loading'
import { ENDPOINTS } from '@/services/api'
import { PAGE_PATHS } from '@/services/paths'
import { loginAgain } from '@/services/userAlerts'

type Props = { token?: string; ids?: string; unityUA?: string }

export default function AuthEmbedded({ token, ids = '', unityUA }: Props) {
  const router = useRouter()
  const { setEmbedded } = useAuth()

  setEmbedded(true)

  useEffect(() => {
    if (!token) return

    const run = async () => {
      const resp = await fetch(`${process.env.API_URL}/${ENDPOINTS.auth_sso}`, {
        method: 'GET',
        headers: {
          'X-Application': 'bioatlas',
          'X-Token-Id': token
        },
        credentials: 'include'
      })

      if (resp.status === 204) {
        router.replace(
          `${PAGE_PATHS.auth}?persist=true&embedded=true&ids=${encodeURIComponent(ids)}${unityUA ? `&x-unity-user-agent=${encodeURIComponent(unityUA)}` : ''}`
        )
        return
      }

      let msg = await resp.text().catch(() => '')
      try {
        msg = JSON.parse(msg).msg ?? msg
      } catch {}
      loginAgain(msg)
      console.error('auth_sso fail:', resp.status, msg)
    }

    run()
  }, [token, ids, unityUA, router])

  return <Loading />
}

export const getServerSideProps: GetServerSideProps<Props> = async ctx => {
  const { query, req } = ctx
  const ids = Array.isArray(query.ids) ? query.ids[0] : query.ids || ''
  const token = Array.isArray(query.token) ? query.token[0] : query.token || ''
  const unityUA = Array.isArray(query['x-unity-user-agent'])
    ? query['x-unity-user-agent'][0]
    : (query['x-unity-user-agent'] as string) || ''

  const hasSession = Boolean(req.cookies['__Secure-IdToken'])

  const qsUnity = unityUA
    ? `&x-unity-user-agent=${encodeURIComponent(unityUA)}`
    : ''

  if (hasSession) {
    console.log('*** [AuthEmbedded] SSR has session')
    return {
      redirect: {
        destination: `/Embedded?ids=${encodeURIComponent(ids)}${qsUnity}`,
        permanent: false
      }
    }
  }

  if (!token) {
    console.log('*** [AuthEmbedded] SSR no token')
    return {
      redirect: {
        destination: `/Signin?ids=${encodeURIComponent(ids)}${qsUnity}`,
        permanent: false
      }
    }
  }

  return { props: { token, ids, unityUA } }
}
